import { AddAlt } from "@carbon/icons-react";
import { cn, type ButtonProps as HeroButtonProps } from "@heroui/react";
import type { CSSProperties } from "react";
import type { ButtonProps } from "./Button";
import {
  ACTIVE_STATE_CLASSES,
  type ButtonColor,
  type ButtonVariant,
} from "./buttonConstants";

export type LinkButtonTriggerProps = Pick<
  { href?: string; to?: string; onPress?: HeroButtonProps["onPress"] },
  "href" | "to" | "onPress"
>;

export function isLinkButton(props: LinkButtonTriggerProps): boolean {
  if (props.href != null) {
    return true;
  }
  return props.to != null && props.onPress == null;
}

export function getDefaultButtonColor(variant: ButtonVariant): ButtonColor {
  return variant === "primary" ? "primary" : "default";
}

export function createAddButton(props: ButtonProps) {
  return {
    "data-testid": "add-button",
    icon: AddAlt,
    ...props,
  };
}

export function getButtonClassName(
  variant: ButtonVariant,
  size: NonNullable<HeroButtonProps["size"]>,
  pillShape?: boolean,
  isActive?: boolean,
  className?: string,
): ReturnType<typeof cn> {
  return cn(
    pillShape ? "rounded-full" : "rounded-md",
    size === "sm" && "px-2 h-7.5",
    size === "md" && "h-9",
    isActive && ACTIVE_STATE_CLASSES[variant],
    className,
  );
}

export function getButtonStyle(
  variant: ButtonVariant,
  color: ButtonColor,
): CSSProperties | undefined {
  // Map the primary color to Hero UI v3 primary which is called "accent"
  const token = color === "primary" ? "accent" : color;

  switch (variant) {
    case "primary":
      // The default grey button uses the stronger neutral — v3's `--default` is
      // too light to read on light surfaces (mirrors the Chip).
      if (color === "default") {
        return {
          "--button-bg": "var(--default-strong)",
          "--button-fg": "var(--default-strong-foreground)",
          "--button-bg-hover": "var(--default-strong-hover)",
          "--button-bg-pressed": "var(--default-strong-hover)",
        } as CSSProperties;
      }

      return {
        "--button-bg": `var(--${token})`,
        "--button-fg": `var(--${token}-foreground)`,
        "--button-bg-hover": `var(--${token}-hover)`,
        "--button-bg-pressed": `var(--${token}-hover)`,
      } as CSSProperties;

    case "secondary":
    case "tertiary": {
      if (color === "default") {
        return {
          "--button-fg":
            "color-mix(in oklab, var(--foreground) 70%, var(--background))",
          borderColor: `color-mix(in oklab, var(--default-strong) 100%, var(--background))`,
          "--button-bg-hover":
            "color-mix(in oklab, var(--foreground) 6%, var(--background))",
        } as CSSProperties;
      }

      return {
        "--button-fg": `var(--${token}-soft-foreground)`,
        borderColor: `color-mix(in oklab, var(--${token}) 45%, var(--background))`,
        "--button-bg-hover": `color-mix(in oklab, var(--${token}) 10%, transparent)`,
        "--button-bg-pressed": `color-mix(in oklab, var(--${token}) 16%, transparent)`,
      } as CSSProperties;
    }

    case "soft":
      // The default grey soft button uses the plain `--default` neutral (v3's
      // `--default-soft` is too light), mirroring the Chip's soft default.
      if (color === "default") {
        return {
          "--button-bg": "var(--default)",
          "--button-fg": "var(--default-foreground)",
          "--button-bg-hover": "var(--default-hover)",
          "--button-bg-pressed": "var(--default-hover)",
        } as CSSProperties;
      }

      return {
        "--button-bg": `var(--${token}-soft)`,
        "--button-fg": `var(--${token}-soft-foreground)`,
        "--button-bg-hover": `var(--${token}-soft-hover)`,
        "--button-bg-pressed": `var(--${token}-soft-hover)`,
      } as CSSProperties;
  }
}
